<template>
	<section class="event-popup">
		<Tabs
			:tabs="eventTabs"
			:selectedIndex="props.eventTab ?? 0"
			@tabSelected="handleTabSelect"
			class="tabs--event"
			:class="`tabs--event-${eventTabs[props.eventTab ?? 0].slug}`"
		>
			<template v-slot:tab1>
				<EventMain
					:infoBtnNotification="infoBtnNotification"
					:upgradesNotification="upgradesNotification"
					:shopNotification="shopNotification"
				/>
			</template>
			<template v-slot:tab2>
				<EventMilestones
					v-if="attributes.type === EventCategory.CHRISTMAS"
				/>
				<EventMarket v-else-if="isEventWithMarket" />
				<EventMilestonesWithPass v-else />
			</template>
			<template v-slot:tab3>
				<EventLeaderboard />
			</template>
		</Tabs>
	</section>
</template>

<script lang="ts" setup>
import EventMain from "@/components/Event/EventMain.vue";
import EventMilestones from "@/components/Event/EventMilestones.vue";
import Tabs from "@/components/Tabs.vue";
import {
	EVENTS_WITH_MILESTONES,
	LAST_SEEN_EVENT_INTRO_ID_KEY,
	LAST_SEEN_EVENT_SHOP_ID_KEY,
	LAST_SEEN_EVENT_UPGRADES_ID_KEY,
} from "@/config/constants";
import { useEventStore } from "@/event";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import { usePopupStore } from "@/store/usePopupStore";
import useUserStore from "@/store/useUserStore.ts";
import type { MilestoneItem } from "@/types/event.ts";
import { MilestoneStatus } from "@/types/event.ts";
import { EventCategory } from "@/types/ui";
import { floorNumber } from "@/utils/number.ts";
import { storeToRefs } from "pinia";
import { computed, markRaw, onMounted, onUnmounted, ref } from "vue";
import EventLeaderboard from "./EventLeaderboard.vue";
import EventMarket from "./EventMarket.vue";
import EventMilestonesWithPass from "./EventMilestonesWithPass.vue";

const emitter = markRaw(useEmitter());

const props = defineProps<{
	eventTab?: number;
}>();

const eventStore = useEventStore();
const { hasUnclaimedMilestones, id, attributes, milestones, grandPrizeState } =
	storeToRefs(eventStore);

const popupStore = usePopupStore();

const userStore = useUserStore();
const { daysAfterRegistration } = storeToRefs(userStore);

const isEventWithMarket = computed((): boolean => {
	return (
		!!attributes.value.type &&
		[EventCategory.SOLAR, EventCategory.PIRATES].includes(
			attributes.value.type,
		)
	);
});

const marketHasNotification = computed((): boolean => {
	if (grandPrizeState.value?.lastDailyFreePickedDaysAfterReg === undefined)
		return false;
	return (
		floorNumber(daysAfterRegistration.value) >
		grandPrizeState.value?.lastDailyFreePickedDaysAfterReg
	);
});

const eventTabs = computed(
	(): Array<{
		name: string;
		slug: string;
		hasNotification?: boolean;
		notificationCount?: number;
	}> => [
		{
			name: "event.event",
			slug: "main",
			hasNotification:
				infoBtnNotification.value !== undefined ||
				upgradesNotification.value !== undefined ||
				shopNotification.value !== undefined,
		},
		isEventWithMarket.value
			? {
					name: "event.market",
					slug: "market",
					hasNotification: marketHasNotification.value,
				}
			: {
					name: "event.milestones",
					slug: "milestones",
					hasNotification: hasUnclaimedMilestones.value,
					notificationCount: claimableMilestonesCount.value,
				},
		{ name: "ui.ranking", slug: "leaderboard" },
	],
);

const hasMilestones = computed((): boolean => {
	if (!attributes.value.type) return false;

	return EVENTS_WITH_MILESTONES.includes(attributes.value.type);
});

const claimableMilestonesCount = computed((): number => {
	if (!hasMilestones.value || !milestones.value) return 0;
	const claimableCount = Array.from(milestones.value.values()).filter(
		(item: MilestoneItem): boolean =>
			item.status === MilestoneStatus.CLAIMABLE,
	).length;

	const claimablePremium = Array.from(milestones.value.values()).filter(
		(item: MilestoneItem): boolean =>
			item.premium?.status === MilestoneStatus.CLAIMABLE,
	).length;

	return claimableCount + claimablePremium;
});

const handleTabSelect = (tab: number): void => {
	popupStore.updatePopup({
		spriteData: {
			slug: `${attributes.value.type}-event`,
			key: tab + 1,
			size: 416,
			hasOverlap: true,
		},
	});
};

const lastSeenEventIntroId = ref<string | null>(
	localStorage.getItem(LAST_SEEN_EVENT_INTRO_ID_KEY),
);
const infoBtnNotification = computed((): string | undefined => {
	if (!id.value) return undefined;
	return lastSeenEventIntroId.value !== id.value ? "!" : undefined;
});

const lastSeenEventUpgradesId = ref(
	localStorage.getItem(LAST_SEEN_EVENT_UPGRADES_ID_KEY),
);
const upgradesNotification = computed((): string | undefined => {
	if (!id.value) return undefined;
	return lastSeenEventUpgradesId.value !== id.value ? "!" : undefined;
});

const lastSeenEventShopId = ref(
	localStorage.getItem(LAST_SEEN_EVENT_SHOP_ID_KEY),
);
const shopNotification = computed((): string | undefined => {
	if (!id.value) return undefined;
	return lastSeenEventShopId.value !== id.value ? "!" : undefined;
});

const updateLocalStorageData = (): void => {
	lastSeenEventIntroId.value = localStorage.getItem(
		LAST_SEEN_EVENT_INTRO_ID_KEY,
	);
	lastSeenEventUpgradesId.value = localStorage.getItem(
		LAST_SEEN_EVENT_UPGRADES_ID_KEY,
	);
	lastSeenEventShopId.value = localStorage.getItem(
		LAST_SEEN_EVENT_SHOP_ID_KEY,
	);
};

onMounted((): void => {
	emitter.on(EmitterEvents.EventLocalStorageUpdated, updateLocalStorageData);
});

onUnmounted((): void => {
	emitter.off(EmitterEvents.EventLocalStorageUpdated, updateLocalStorageData);
});
</script>

<style lang="scss" scoped>
.event-popup {
	height: 30rem; // IMPORTANT: Required for scaling
}
</style>
